import {
  GD_DESIGN_MODULE_CODE,
  GD_VIP_VERSION,
  MATERIAL_MOUDLE_CODE,
  PLUS_VIP_MODULE_CODE,
  SOLUTION_VERSION,
} from "@/constants/gaodingVip";
import { ORGS_CLASS_MAP } from "@/pageComponents/GdMerge/shared";
import { filter, isNaN } from "lodash";

export function getGdDesignVipAuth(gddesignVip) {
  // FREE-团队免费版、BASIC-标准版、PROFESSIONAL-专业版、FLAGSHIP-旗舰版
  // INDIVIDUAL_FREE-个人免费版 INDIVIDUAL_PROFESSIONAL_INDIVIDUAL-个人专业版-个人授权
  // INDIVIDUAL_PROFESSIONAL_ENTERPRISE-个人专业版-企业授权
  const GdDesignVersionMap = {
    FREE: "团队版",
    BASIC: "团队版·企业授权",
    PROFESSIONAL: "团队版·企业授权",
    FLAGSHIP: "企业版·企业定制授权",
    // 个人免费版
    INDIVIDUAL_FREE: "个人版",
    // 基础版
    INDIVIDUAL_PROFESSIONAL_INDIVIDUAL: "基础版·个人授权",
    INDIVIDUAL_PROFESSIONAL_ENTERPRISE: "基础版·企业授权",
  };

  const product_type = gddesignVip.product_info.product_type || "";

  return GdDesignVersionMap[product_type];
}
export function getVersionCode(info: IGdOrgInfo): keyof typeof GD_VIP_VERSION {
  // FREE-团队免费版、BASIC-团队版、PROFESSIONAL-专业版、FLAGSHIP-企业版、INDIVIDUAL_FREE-个人免费版、INDIVIDUAL-基础版
  const GdVersionMap = {
    FREE: "freeTeam",
    BASIC: "team",
    PROFESSIONAL: "team",
    FLAGSHIP: "enterprise",
    // 个人免费版
    INDIVIDUAL_FREE: "free",
    // 基础版
    INDIVIDUAL: "basic",
  };

  const type = info.product?.base_product_type;

  let versionCode: keyof typeof GD_VIP_VERSION = GdVersionMap[type] || "free";

  // 大会员判断
  if (info.product?.vip_type === "PLUS") {
    versionCode += "VipPlus";
  }

  return versionCode;
}

export function getGdProductDetail(info: IGdOrgInfo) {
  const versionCode = getVersionCode(info);

  /**稿定模板一体化会员信息 */
  const gdesignProductMod = info.modules?.find?.(
    (item) =>
      item.module_info.code === GD_DESIGN_MODULE_CODE &&
      item.product_info.expire_date > 0
  );

  /**素材一体化会员信息 */
  const materialProductMod = info.modules?.find?.(
    (item) =>
      item.module_info.code === MATERIAL_MOUDLE_CODE &&
      item.product_info.expire_date > 0
  );

  /**是否是大会员 */
  const isPlus = info.product?.vip_type === "PLUS";

  return {
    /**一体化版本code:
     * free:免费版，basic: 基础版，team: 团队版 enterprise: 企业版；  vipPlus：有这个后缀表示是大会员版本
     * */
    code: versionCode,
    ...GD_VIP_VERSION[versionCode],
    /**是否是大会员 */
    isPlus,
    /**稿定模板一体化会员信息 */
    gdesignProductMod,
    /**素材一体化会员信息 */
    materialProductMod,
  };
}

export function isPersonalTypeByCode(code: ReturnType<typeof getVersionCode>) {
  return code === "basic" || code === "basicVipPlus" || code === "free";
}

export function isBasicTypeByCode(code: ReturnType<typeof getVersionCode>) {
  return code === "basic" || code === "basicVipPlus";
}

export function isNoAuthByGdVip(vips: IGdVip[]) {
  return vips.some((item) =>
    [
      SOLUTION_VERSION.PERSONAL_LEARNING,
      SOLUTION_VERSION.HUA_BAN_TEMPLATE_LEARNING,
    ].includes(item.solution_version)
  );
}

export function isBigCompanyByGdVip(vips: IGdVip[]) {
  return (vips || []).some(
    (item) => item.solution_version === SOLUTION_VERSION.FLAGSHIP
  );
}

export function isBigCompanyByOrg(org: IGdOrgInfo) {
  return (
    org?.product?.product_type === "FLAGSHIP" ||
    org?.product?.base_product_type === "FLAGSHIP"
  );
}

/**筛选素材会员 */
export function filterMaterailVip(
  vips: IGdVip[],
  option: {
    /**是否返回包含过期会员 */
    includeExpired?: boolean;
  } = {}
) {
  return filter(
    vips || [],
    (item) =>
      (option?.includeExpired ? true : !item.is_expired) &&
      (item.module_code === MATERIAL_MOUDLE_CODE ||
        item.module_code === PLUS_VIP_MODULE_CODE)
  );
}

/**筛选稿定会员信息 */
export function filterGdDesignVip(
  vips: IGdVip[],
  option: {
    /**是否返回包含过期会员 */
    includeExpired?: boolean;
  } = {}
) {
  return filter(
    vips || [],
    (item) =>
      (option?.includeExpired ? true : !item.is_expired) &&
      (item.module_code === GD_DESIGN_MODULE_CODE ||
        item.module_code === PLUS_VIP_MODULE_CODE)
  );
}

/**
 * 用于解析地址hash字符串，获取打开会员弹窗的参数
 * 使用场景: 链接唤起支付弹窗
 * @param hash buyVip-personal-gerenvip_2024.1m-1
 * https://doc.huanleguang.com/pages/viewpage.action?pageId=250410891
 */
export function parseVipUrlHash(hash: string) {
  const [action, type, pkgs = "", versionTab] = (hash || "").split("-");
  const [selectedPkg, selectedPlusSku] = pkgs.split("+");

  let selectSkuId, buyType, source;
  if (!isNaN(+type)) {
    selectSkuId = +type;
    buyType = pkgs;
    source = versionTab;
  }

  return {
    /**购买弹窗类型 */
    action,
    /**个人还是企业 */
    type,
    /**选中的商品 */
    selectedPkg,
    /**选中的换购商品 */
    selectedPlusSku,
    /**选中的版本：0,1，对应学习授权与个人商用授权， 企业标准授权与高级授权 */
    versionTab,
    /**稿定使用： 选中商品skuid */
    selectSkuId,
    /**稿定使用，指定大会员弹窗 */
    buyType,
    /**稿定使用，指定来源，用于数据分析*/
    source,
  };
}

export function isPlusByGdVip(vips: IGdVip[]) {
  return (vips || []).some((item) => item.module_code === PLUS_VIP_MODULE_CODE);
}

export function isPersonalOrg(orgInfo: IGdOrgInfo) {
  return orgInfo?.orgClass === ORGS_CLASS_MAP.PERSONAL;
}
